import { ReactElement, ReactNode } from 'react'

import { Alert, AlertProps } from 'antd'
import classnames from 'classnames'

import { IconButton } from 'components/Buttons'
import { Flex, Icon } from 'components/core'
import usePersistedDismiss from 'hooks/usePersistedDismiss'

import * as styles from './styles.scss'

type DismissableAlertProps = AlertProps & {
    storageKey: string
    numberOfDaysToHideAfterDismiss?: number
    className?: string
    endActions?: ReactNode
}

const DismissableAlert = ({
    storageKey,
    numberOfDaysToHideAfterDismiss = undefined,
    className,
    type = 'info',
    message,
    endActions,
    ...otherAlertProps
}: DismissableAlertProps): ReactElement | null => {
    const { dismiss, isDismissed } = usePersistedDismiss(
        storageKey,
        numberOfDaysToHideAfterDismiss
    )

    if (isDismissed) {
        return null
    }

    const dismissButton = (
        <IconButton
            icon={<Icon name="PLUS" rotate={45} size={13} />}
            onClick={dismiss}
            size="small"
            type="link"
        />
    )

    const messageBody = (
        <Flex justifyContent="space-between" alignItems="center">
            <Flex>{message}</Flex>
            {endActions != null ? (
                <Flex gap={12} alignItems="center">
                    {endActions}
                    {dismissButton}
                </Flex>
            ) : (
                dismissButton
            )}
        </Flex>
    )

    return (
        <Alert
            className={classnames(
                styles.alert,
                {
                    [styles['alert-info']]: type === 'info',
                },
                className
            )}
            message={messageBody}
            type={type}
            {...otherAlertProps}
        />
    )
}

export default DismissableAlert
